/* eslint-disable max-lines */
import { get } from "@src/appV2/api";
import { API_TYPES } from "@src/appV2/api/constants";
import { environmentConfig } from "@src/appV2/environment";
import { type Shift } from "@src/appV2/Shifts/Shift/types";
import {
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";

import {
  type GetInterviewsRequest as GetInterviewsRequestType,
  getInterviewsRequestSchema as getInterviewsRequestSchemaValue,
  type GetInterviewsResponse as GetInterviewsResponseType,
  getInterviewsResponseSchema as getInterviewsResponseSchemaValue,
  type Interview as InterviewRecord,
  INTERVIEW_ATTENDANCE_STATUS as INTERVIEW_ATTENDANCE_STATUS_VALUE,
  INTERVIEW_ATTENDANCE_VERIFICATION_STATUS as INTERVIEW_ATTENDANCE_VERIFICATION_STATUS_VALUE,
  INTERVIEW_SCHEDULE_STATUS as INTERVIEW_SCHEDULE_STATUS_VALUE,
  INTERVIEW_TYPE as INTERVIEW_TYPE_VALUE,
  INTERVIEW_VERIFICATION_STATUS as INTERVIEW_VERIFICATION_STATUS_VALUE,
  type InterviewAttendanceStatus as InterviewAttendanceStatusType,
  type InterviewAttendanceVerificationStatus as InterviewAttendanceVerificationStatusType,
  type InterviewIncluded as InterviewIncludedType,
  type InterviewIncludedPlacement as InterviewIncludedPlacementType,
  type InterviewIncludedPlacementCandidateBan as InterviewIncludedPlacementCandidateBanType,
  interviewIncludedSchema as interviewIncludedSchemaValue,
  type InterviewIncludedWorker as InterviewIncludedWorkerType,
  type InterviewIncludedWorkplace as InterviewIncludedWorkplaceType,
  type InterviewScheduleStatus as InterviewScheduleStatusType,
  interviewSchema as interviewSchemaValue,
  type InterviewStatus as InterviewStatusType,
  InterviewStatus as InterviewStatusValue,
  type InterviewType as InterviewTypeType,
  type InterviewVerificationStatus as InterviewVerificationStatusType,
} from "./interviewSchemas";

export const getInterviewsRequestSchema = getInterviewsRequestSchemaValue;
export const getInterviewsResponseSchema = getInterviewsResponseSchemaValue;
/* eslint-disable unicorn/prefer-export-from -- Keep compatibility exports without turning this hook into a barrel. */
export const INTERVIEW_ATTENDANCE_STATUS = INTERVIEW_ATTENDANCE_STATUS_VALUE;
export const INTERVIEW_ATTENDANCE_VERIFICATION_STATUS =
  INTERVIEW_ATTENDANCE_VERIFICATION_STATUS_VALUE;
export const INTERVIEW_SCHEDULE_STATUS = INTERVIEW_SCHEDULE_STATUS_VALUE;
export const INTERVIEW_TYPE = INTERVIEW_TYPE_VALUE;
export const INTERVIEW_VERIFICATION_STATUS = INTERVIEW_VERIFICATION_STATUS_VALUE;
export const InterviewStatus = InterviewStatusValue;
export const interviewIncludedSchema = interviewIncludedSchemaValue;
export const interviewSchema = interviewSchemaValue;
/* eslint-enable unicorn/prefer-export-from */
export type GetInterviewsRequest = GetInterviewsRequestType;
export type GetInterviewsResponse = GetInterviewsResponseType;
export type Interview = InterviewRecord;
export type InterviewAttendanceStatus = InterviewAttendanceStatusType;
export type InterviewAttendanceVerificationStatus = InterviewAttendanceVerificationStatusType;
export type InterviewIncluded = InterviewIncludedType;
export type InterviewIncludedPlacement = InterviewIncludedPlacementType;
export type InterviewIncludedPlacementCandidateBan = InterviewIncludedPlacementCandidateBanType;
export type InterviewIncludedWorker = InterviewIncludedWorkerType;
export type InterviewIncludedWorkplace = InterviewIncludedWorkplaceType;
export type InterviewScheduleStatus = InterviewScheduleStatusType;
export type InterviewStatus = InterviewStatusType;
export type InterviewType = InterviewTypeType;
export type InterviewVerificationStatus = InterviewVerificationStatusType;

export const interviewsApiUrl = `${environmentConfig.REACT_APP_BASE_API_URL}/interviews`;

interface FetchInterviewsParams {
  sort?: string | undefined;
  filter: GetInterviewsRequest["filter"];
  pageSize?: number;
  include?: string;
}

export function useFetchPaginatedInterviews(
  params: FetchInterviewsParams,
  options: UseInfiniteQueryOptions<GetInterviewsResponse, AxiosError> = {}
) {
  const { filter, sort, pageSize = 100, include } = params;

  return useInfiniteQuery({
    queryKey: [interviewsApiUrl, filter, pageSize],
    queryFn: async ({
      pageParam: pageParameter = undefined,
    }: QueryFunctionContext<QueryKey, string | undefined>) => {
      const nonEmptyFilter = Object.fromEntries(
        Object.entries(filter ?? {}).filter(([_, value]) => value)
      );
      const response = await get({
        url: interviewsApiUrl,
        queryParams: {
          page: {
            size: pageSize,
            cursor: pageParameter,
          },
          filter: nonEmptyFilter,
          sort,
          include,
        },
        responseSchema: getInterviewsResponseSchema,
        requestSchema: getInterviewsRequestSchema,
      });

      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage.links?.nextCursor ?? undefined,
    ...options,
  });
}

export function transformResponseIntoInterview(
  interview: Interview,
  includedMetadata: InterviewIncluded,
  workerTimezone: string
) {
  const workplace = includedMetadata.find(
    (included): included is InterviewIncludedWorkplace =>
      included.type === API_TYPES.workplace &&
      included.id === interview.relationships.workplace.data.id
  );

  const worker = includedMetadata.find(
    (included): included is InterviewIncludedWorker =>
      included.type === API_TYPES.workplace &&
      included.id === interview.relationships.workplace.data.id
  );

  const placement = includedMetadata.find(
    (included): included is InterviewIncludedPlacement =>
      included.type === API_TYPES.placement &&
      included.id === interview.relationships.placement.data.id
  );

  const placementCandidateBan = includedMetadata.find(
    (included): included is InterviewIncludedPlacementCandidateBan =>
      included.type === API_TYPES.placementCandidateBan &&
      included.id === interview.relationships.placementCandidateBan?.data.id
  );

  return {
    id: interview.id,
    type: interview.type,
    ...interview.attributes,
    scheduleStatus: interview.meta?.scheduleStatus,
    isBanExcused: placementCandidateBan?.meta.isExcused ?? false,
    start: utcToZonedTime(interview.attributes.start, workerTimezone),
    end: utcToZonedTime(interview.attributes.end, workerTimezone),
    placementId: interview.relationships?.placement?.data?.id,
    placementApplicationId: interview.relationships?.placementApplication?.data?.id,
    ...(workplace && {
      workplace: {
        id: workplace.id,
        name: workplace.attributes.name,
        formattedAddress: workplace.attributes.formattedAddress,
        distance: workplace.attributes.distance,
        phone: workplace.attributes.phone,
      },
    }),
    ...(placement && {
      placement: {
        id: placement.id,
        workerTypes: placement.attributes.workerTypes,
      },
    }),
    ...(worker ? { worker: { id: worker.id, ...worker.attributes } } : undefined),
    placementCandidateId: interview.relationships?.placementCandidate?.data?.id,
  };
}

export type InterviewWithDetails = ReturnType<typeof transformResponseIntoInterview>;

export type Bookings =
  | { type: "shift"; data: Shift }
  | { type: "interview"; data: InterviewWithDetails };
/* eslint-enable max-lines */
